import { GlobalSearchTab } from "@/models/search/Search";
import { Search } from "@/models/search/Search";
import { logger } from "@/lib/logger";

export const buildSearchUrlParams = (
  searchQuery: Search | null,
  overrideSearchTerm?: string
) => {
  const params = new URLSearchParams();

  // Use override search term if provided, otherwise use search query
  const searchTerm = overrideSearchTerm ?? searchQuery?.searchTerm;
  if (searchTerm) {
    params.set("searchQuery", searchTerm);
  }

  // Add search tags as comma-separated string
  if (searchQuery?.searchTags && searchQuery.searchTags.length > 0) {
    params.set("searchTags", searchQuery.searchTags.join(","));
  }

  if (searchQuery?.tab) {
    params.set("tab", searchQuery.tab);
  }

  // Filters - legislation
  if (searchQuery?.filters && Object.keys(searchQuery.filters).length > 0) {
    params.set("filters", JSON.stringify(searchQuery.filters));
  }

  // Workspace document filters (metadata + document_title)
  if (
    searchQuery?.workspaceDocumentFilters &&
    Object.keys(searchQuery.workspaceDocumentFilters).length > 0
  ) {
    params.set(
      "workspaceDocumentFilters",
      JSON.stringify(searchQuery.workspaceDocumentFilters)
    );
  }

  return params;
};

export const parseSearchUrlParams = (
  searchUrl: string,
  id?: string
): Search => {
  let url: URL;

  try {
    // Try to parse as absolute URL first
    url = new URL(searchUrl);
  } catch {
    // If it fails, treat as relative URL
    url = new URL(searchUrl, window.location.origin);
  }

  const params = url.searchParams;

  // Parse search tags from comma-separated string
  const tagsParam = params.get("searchTags");
  const searchTags = tagsParam
    ? tagsParam.split(",").filter((tag) => tag.trim() !== "")
    : undefined;

  const search: Search = {
    id: id,
    searchTerm: params.get("searchQuery") || "",
    searchTags: searchTags,
    tab: (params.get("tab") as GlobalSearchTab) || GlobalSearchTab.All,
  };

  // Parse filters - support both new "filters" param and legacy "legislationFilters" for backward compatibility
  const filtersParam = params.get("filters");
  const legacyLegislationFilters = params.get("legislationFilters");

  if (filtersParam) {
    try {
      search.filters = JSON.parse(filtersParam);
    } catch {
      // No `error` here or below: a JSON.parse message can quote the input,
      // which holds what the user searched for.
      logger.warn("Failed to parse search URL filters, using none", {
        operation: "parseSearchUrlParams",
        param: "filters",
      });
      search.filters = {};
    }
  } else if (legacyLegislationFilters) {
    // Handle legacy legislationFilters parameter for backward compatibility
    try {
      search.filters = JSON.parse(legacyLegislationFilters);
    } catch {
      logger.warn("Failed to parse search URL filters, using none", {
        operation: "parseSearchUrlParams",
        param: "legislationFilters",
      });
      search.filters = {};
    }
  } else {
    search.filters = {};
  }

  const wdFiltersParam = params.get("workspaceDocumentFilters");
  if (wdFiltersParam) {
    try {
      search.workspaceDocumentFilters = JSON.parse(wdFiltersParam);
    } catch {
      logger.warn("Failed to parse search URL filters, using none", {
        operation: "parseSearchUrlParams",
        param: "workspaceDocumentFilters",
      });
      search.workspaceDocumentFilters = {};
    }
  } else {
    search.workspaceDocumentFilters = {};
  }

  return search;
};
